// ─────────────────────────────────────────────────────────────────────────────
// api.js — Crew Portal API client
//
// Centralises all fetch calls to the Node/Express backend.
// Token is stored in memory (React state in App.js) and injected here.
//
// Usage:
//   import api from './api';
//   api.setToken(token);                         // after login
//   const data = await api.get('/auth/me');
//   const data = await api.post('/auth/login', { email, password });
// ─────────────────────────────────────────────────────────────────────────────

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// ── Company <-> API shape helpers ────────────────────────────────────────────

/** Ensure a company object always has a `contacts` array and a `type`. */
export const normalizeCompany = (c = {}) => ({
  type: 'Client',
  ...c,
  contacts: Array.isArray(c.contacts) && c.contacts.length
    ? c.contacts
    : (c.contactName || c.contactEmail || c.contactPhone)
      ? [{
          id: `${c.id || 'contact'}-primary`,
          name:  c.contactName  || '',
          email: c.contactEmail || '',
          phone: c.contactPhone || '',
          role:  '',
        }]
      : [],
});

// ── Project <-> API shape helpers ────────────────────────────────────────────
// БД проєктів має плоскі колонки + finance_extra JSONB. Деталі форми
// (persons, finance, assigned, equipment) живуть у finance_extra.

const PROJECT_EXTRA_KEYS = ['persons', 'finance', 'assigned', 'equipment', 'docFolders', 'paymentStatus'];

/** Прибрати несеріалізовні File-обʼєкти (_file) з дерева папок документів,
 * а також linkedDocuments/freelancerId — це computed-поля, які бекенд
 * підмішує в docFolders.crew.<ім'я> на кожен GET (профільні документи
 * фрілансера, живі, не копії — див. mergeLinkedDocsIntoDocFolders на
 * бекенді). Якщо їх не прибрати тут, вони б застигли стрічкою в
 * finance_extra.docFolders при першому ж збереженні форми. */
export const stripDocFiles = (folders) => (folders || []).map(({ linkedDocuments, freelancerId, ...f }) => ({
  ...f,
  files: (f.files || []).map(({ _file, ...rest }) => rest),
  subfolders: stripDocFiles(f.subfolders || []),
}));

/** При читанні: лишити тільки реальні файли (з serverId) — фантоми/демо геть. */
const cleanDocTree = (folders) => (folders || []).map(f => ({
  ...f,
  files: (f.files || []).filter(x => x.serverId),
  subfolders: cleanDocTree(f.subfolders || []),
}));

export const normalizeProject = (p = {}) => {
  const extra = p.financeExtra || {};
  const out = { ...p };
  for (const k of PROJECT_EXTRA_KEYS) {
    if (out[k] === undefined && extra[k] !== undefined) out[k] = extra[k];
  }
  out.persons       = Array.isArray(out.persons)       ? out.persons       : [];
  out.assigned      = Array.isArray(out.assigned)      ? out.assigned      : [];
  out.equipment     = Array.isArray(out.equipment)     ? out.equipment     : [];
  out.paymentStatus = Array.isArray(out.paymentStatus) ? out.paymentStatus : [];
  // Postgres DATE приходить як повний timestamp — лишаємо тільки YYYY-MM-DD
  if (out.startDate) out.startDate = String(out.startDate).slice(0, 10);
  if (out.endDate)   out.endDate   = String(out.endDate).slice(0, 10);
  // Profit не зберігається у БД — похідне від income/outcome
  if (out.profit === undefined || out.profit === null) {
    out.profit = (Number(out.income) || 0) - (Number(out.outcome) || 0);
  }
  out.currency = out.currency || 'EUR';
  if (Array.isArray(out.docFolders)) out.docFolders = cleanDocTree(out.docFolders);
  return out;
};

const serializeProject = (data = {}) => {
  const extra = { ...(data.financeExtra || {}) };
  for (const k of PROJECT_EXTRA_KEYS) {
    if (data[k] !== undefined) extra[k] = data[k];
  }
  if (extra.docFolders) extra.docFolders = stripDocFiles(extra.docFolders);
  return { ...data, financeExtra: extra };
};

/** Map the UI `contacts` array onto the flat fields the backend stores. */
const serializeCompany = (data = {}) => {
  const first = (Array.isArray(data.contacts) ? data.contacts : [])[0] || {};
  return {
    ...data,
    contactName:  first.name  || '',
    contactEmail: first.email || '',
    contactPhone: first.phone || '',
  };
};

let _token = null;

const api = {
  /** Store the JWT in memory (called by App.js after login). */
  setToken(token) {
    _token = token;
  },

  /** Clear the token (called on logout). */
  clearToken() {
    _token = null;
  },

  /** Current JWT (used to bootstrap a session in a tab opened via target="_blank"). */
  getToken() {
    return _token;
  },

  /** Generic request helper. */
  async request(method, path, body) {
    const headers = { 'Content-Type': 'application/json' };
    if (_token) headers['Authorization'] = `Bearer ${_token}`;

    const response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const err = new Error(data.error || `HTTP ${response.status}`);
      err.status = response.status;
      err.data   = data;
      throw err;
    }

    return data;
  },

  get:    (path)        => api.request('GET',    path),
  post:   (path, body)  => api.request('POST',   path, body),
  patch:  (path, body)  => api.request('PATCH',  path, body),
  delete: (path)        => api.request('DELETE', path),

  /** Multipart upload (FormData) — без Content-Type, браузер ставить сам. */
  async upload(path, formData) {
    const headers = {};
    if (_token) headers['Authorization'] = `Bearer ${_token}`;
    const response = await fetch(`${BASE_URL}${path}`, { method: 'POST', headers, body: formData });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.error || `HTTP ${response.status}`);
      err.status = response.status;
      throw err;
    }
    return data;
  },

  /** Абсолютний URL файлу з токеном у query (для <img src>/<iframe> перегляду).
   *  { download: true } додає ?download=1 — бекенд віддасть Content-Disposition:
   *  attachment замість inline навіть для картинок/PDF (кнопка "Download file"). */
  fileUrl(path, { download } = {}) {
    const base = `${BASE_URL}${path}${path.includes('?') ? '&' : '?'}token=${encodeURIComponent(_token || '')}`;
    return download ? `${base}&download=1` : base;
  },

  // ── Auth convenience methods ──────────────────────────────────────────────

  auth: {
    login: (email, password) =>
      api.post('/auth/login', { email, password }),

    register: (data) =>
      api.post('/auth/register', data),

    forgotPassword: (email) =>
      api.post('/auth/forgot-password', { email }),

    resetPassword: (token, newPassword) =>
      api.post('/auth/reset-password', { token, newPassword }),

    me: () =>
      api.get('/auth/me'),

    // Запрошення "довідникової" картки фрілансера на портал (без окремого Approve)
    verifyInvite:  (token)           => api.get(`/auth/invite/${token}`),
    acceptInvite:  (token, password) => api.post('/auth/accept-invite', { token, password }),
  },

  // ── Users convenience methods ─────────────────────────────────────────────

  users: {
    list:           (params = {}) => api.get(`/users?${new URLSearchParams(params)}`),
    get:            (id)          => api.get(`/users/${id}`),
    update:         (id, data)    => api.patch(`/users/${id}`, data),
    changePassword: (id, data)    => api.patch(`/users/${id}/password`, data),
    approve:        (id, role)    => api.patch(`/users/${id}/approve`, { role }),
    reject:         (id)          => api.patch(`/users/${id}/reject`),
    delete:         (id)          => api.delete(`/users/${id}`),
  },

  // ── Freelancers ───────────────────────────────────────────────────────────
  freelancers: {
    list:         (params = {}) => api.get(`/freelancers?${new URLSearchParams(params)}`),
    get:          (id)          => api.get(`/freelancers/${id}`),
    create:       (data)        => api.post('/freelancers', data),
    update:       (id, data)    => api.request('PUT', `/freelancers/${id}`, data),
    delete:       (id)          => api.delete(`/freelancers/${id}`),
    setStatus:    (id, status)  => api.patch(`/freelancers/${id}/status`, { status }),

    // Self-service (роль Freelancer, власна картка)
    me:       ()     => api.get('/freelancers/me'),
    updateMe: (data) => api.patch('/freelancers/me', data),

    addMyPassport:    (data)       => api.post('/freelancers/me/passports', data),
    updateMyPassport: (id, data)   => api.patch(`/freelancers/me/passports/${id}`, data),
    deleteMyPassport: (id)         => api.delete(`/freelancers/me/passports/${id}`),

    uploadMyDocuments: (category, fileList, extra = {}) => {
      const fd = new FormData();
      [...fileList].forEach(f => fd.append('files', f));
      fd.append('category', category);
      if (extra.title)  fd.append('title', extra.title);
      if (extra.expiry) fd.append('expiry', extra.expiry);
      return api.upload('/freelancers/me/documents', fd);
    },
    deleteMyDocument: (docId) => api.delete(`/freelancers/me/documents/${docId}`),
    // Заміна позначеного (🚩) документа одним кроком: новий файл вантажиться,
    // старий видаляється сам — без ручного delete+upload через Profile.
    replaceMyDocument: (docId, file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload(`/freelancers/me/documents/${docId}/replace`, fd);
    },
    documentUrl:      (freelancerId, docId, opts) => api.fileUrl(`/freelancers/${freelancerId}/documents/${docId}/download`, opts),

    // Особистий календар (self-service): дні недоступності / нотатки-події
    myCalendarEntries:     ()      => api.get('/freelancers/me/calendar-entries'),
    addMyCalendarEntry:    (data)  => api.post('/freelancers/me/calendar-entries', data),
    deleteMyCalendarEntry: (id)    => api.delete(`/freelancers/me/calendar-entries/${id}`),

    // Admin/HR/PM — усі особисті записи всіх фрілансерів (командний календар)
    allCalendarEntries:    ()      => api.get('/freelancers/calendar-entries'),

    // Обов'язкова інструкція ("Got it") + електронний підпис згоди на обробку даних
    ackGuide:    ()     => api.patch('/freelancers/me/guide-ack'),
    signConsent: (data) => api.post('/freelancers/me/consent', data),

    // Admin/HR — ручне нагадування фрілансеру, який не завершив реєстрацію
    remind: (id) => api.post(`/freelancers/${id}/remind`),
    // Admin/HR — нагадування усім фрілансерам з незавершеним onboarding одразу
    remindAll: () => api.post('/freelancers/remind-all'),

    // Admin/HR — запросити "довідникову" картку (без userId) на портал; той
    // самий виклик і для повторного надсилання (Resend)
    invite: (id) => api.post(`/freelancers/${id}/invite`),

    // Admin/HR/PM — позначити документ як "не відповідає вимогам" (reason='' знімає позначку)
    flagDocument: (freelancerId, docId, reason) =>
      api.patch(`/freelancers/${freelancerId}/documents/${docId}/flag`, { reason }),
    // Admin/HR/PM — один лист з усіма позначеними документами цього фрілансера
    notifyFlaggedDocuments: (freelancerId) => api.post(`/freelancers/${freelancerId}/notify-flagged-documents`),

    // Admin/HR/PM — документи довільного фрілансера
    uploadDocuments: (freelancerId, category, fileList, extra = {}) => {
      const fd = new FormData();
      [...fileList].forEach(f => fd.append('files', f));
      fd.append('category', category);
      if (extra.title)  fd.append('title', extra.title);
      if (extra.expiry) fd.append('expiry', extra.expiry);
      return api.upload(`/freelancers/${freelancerId}/documents`, fd);
    },
    deleteDocument: (freelancerId, docId) => api.delete(`/freelancers/${freelancerId}/documents/${docId}`),

    // Self-service — сповіщення "документи у вашій папці на проєкті X оновлено"
    // (модалка при вході, поки не позначено побаченим).
    myDocNotifications:       (unseen)     => api.get(`/freelancers/me/doc-notifications${unseen ? '?unseen=1' : ''}`),
    markDocNotificationsSeen: (ids)        => api.post('/freelancers/me/doc-notifications/mark-seen', ids ? { ids } : {}),
  },

  // ── Activity log ──────────────────────────────────────────────────────────
  // Admin/SuperAdmin only — журнал дій на Dashboard (під Pending Actions) +
  // окрема повна сторінка з пагінацією.
  activityLog: {
    recent: (limit = 10) => api.get(`/activity-log/recent?limit=${limit}`),
    list:   (params = {}) => api.get(`/activity-log?${new URLSearchParams(params)}`),
  },

  // ── Companies ─────────────────────────────────────────────────────────────
  // The backend stores a single flat contact (contact_name/email/phone) and no
  // `type` column, while the UI works with a `contacts` array and `type`.
  // Normalize on read / serialize on write so the UI never sees `undefined`.
  companies: {
    list: async (params = {}) => {
      const res = await api.get(`/companies?${new URLSearchParams(params)}`);
      return { ...res, companies: (res.companies || []).map(normalizeCompany) };
    },
    get: async (id) => {
      const res = await api.get(`/companies/${id}`);
      return { ...res, company: normalizeCompany(res.company) };
    },
    create: async (data) => {
      const res = await api.post('/companies', serializeCompany(data));
      // Keep full contacts/type from the form; backend only echoes flat fields.
      return { ...res, company: normalizeCompany({ ...data, ...res.company }) };
    },
    update: async (id, data) => {
      const res = await api.request('PUT', `/companies/${id}`, serializeCompany(data));
      return { ...res, company: normalizeCompany({ ...data, ...res.company }) };
    },
    delete: (id) => api.delete(`/companies/${id}`),
  },

  // ── Projects ──────────────────────────────────────────────────────────────
  // Деталі (persons, finance, assigned, equipment) БД зберігає у finance_extra
  // (JSONB). Пакуємо на записі, розпаковуємо на читанні.
  projects: {
    list: async (params = {}) => {
      const res = await api.get(`/projects?${new URLSearchParams(params)}`);
      return { ...res, projects: (res.projects || []).map(normalizeProject) };
    },
    get: async (id) => {
      const res = await api.get(`/projects/${id}`);
      return { ...res, project: normalizeProject(res.project) };
    },
    create: async (data) => {
      const res = await api.post('/projects', serializeProject(data));
      return { ...res, project: normalizeProject(res.project) };
    },
    update: async (id, data) => {
      const res = await api.request('PUT', `/projects/${id}`, serializeProject(data));
      return { ...res, project: normalizeProject(res.project) };
    },
    delete:    (id)          => api.delete(`/projects/${id}`),
    setStatus: (id, status)  => api.patch(`/projects/${id}/status`, { status }),
    archive:   (id, archived)=> api.patch(`/projects/${id}/archive`, { archived }),

    // Self-service (роль Freelancer) — тільки призначені проєкти, без фінансів
    mine: () => api.get('/projects/mine'),

    listFiles:   (id)           => api.get(`/projects/${id}/files`),
    uploadFiles: (id, fileList) => {
      const fd = new FormData();
      [...fileList].forEach(f => fd.append('files', f));
      return api.upload(`/projects/${id}/files`, fd);
    },
    deleteFile:  (id, fileId)   => api.delete(`/projects/${id}/files/${fileId}`),
    fileUrl:     (id, fileId, opts) => api.fileUrl(`/projects/${id}/files/${fileId}/download`, opts),

    listComments:   (id)              => api.get(`/projects/${id}/comments`),
    addComment:     (id, text)        => api.post(`/projects/${id}/comments`, { text }),
    deleteComment:  (id, commentId)   => api.delete(`/projects/${id}/comments/${commentId}`),

    // Self-service (роль Freelancer)
    confirmMyAssignment: (id, status, reason) => api.post(`/projects/${id}/my-assignment/confirm`, { status, reason }),
    uploadMyCrewFolderFiles: (id, fileList, folderId) => {
      const fd = new FormData();
      [...fileList].forEach(f => fd.append('files', f));
      if (folderId) fd.append('folderId', folderId);
      return api.upload(`/projects/${id}/my-crew-folder/documents`, fd);
    },

    // Self-service (роль Freelancer) — керування папками: своя Crew/<Ім'я>
    // (повний CRUD, крім кореня) і Project Documents (лише свої папки/файли).
    createMyCrewFolder:  (id, name, parentFolderId) => api.post(`/projects/${id}/my-crew-folder/folders`, { name, parentFolderId }),
    renameMyCrewFolder:  (id, folderId, name)        => api.patch(`/projects/${id}/my-crew-folder/folders/${folderId}`, { name }),
    deleteMyCrewFolder:  (id, folderId)              => api.delete(`/projects/${id}/my-crew-folder/folders/${folderId}`),
    deleteMyCrewFile:    (id, fileId)                => api.delete(`/projects/${id}/my-crew-folder/files/${fileId}`),

    createProjectDocFolder: (id, name, parentFolderId) => api.post(`/projects/${id}/project-documents/folders`, { name, parentFolderId }),
    renameProjectDocFolder: (id, folderId, name)        => api.patch(`/projects/${id}/project-documents/folders/${folderId}`, { name }),
    deleteProjectDocFolder: (id, folderId)              => api.delete(`/projects/${id}/project-documents/folders/${folderId}`),
    deleteProjectDocFile:   (id, fileId)                => api.delete(`/projects/${id}/project-documents/files/${fileId}`),
    uploadProjectDocFiles:  (id, folderId, fileList) => {
      const fd = new FormData();
      [...fileList].forEach(f => fd.append('files', f));
      return api.upload(`/projects/${id}/project-documents/folders/${folderId}/documents`, fd);
    },
  },

  // ── Tasks ─────────────────────────────────────────────────────────────────
  tasks: {
    list:   (params = {}) => api.get(`/tasks?${new URLSearchParams(params)}`),
    create: (data)         => api.post('/tasks', data),
    update: (id, data)     => api.patch(`/tasks/${id}`, data),
    delete: (id)           => api.delete(`/tasks/${id}`),
  },

  // ── Equipment ─────────────────────────────────────────────────────────────
  equipment: {
    list:   (params = {}) => api.get(`/equipment?${new URLSearchParams(params)}`),
    get:    (id)          => api.get(`/equipment/${id}`),
    create: (data)        => api.post('/equipment', data),
    update: (id, data)    => api.request('PUT', `/equipment/${id}`, data),
    delete: (id)          => api.delete(`/equipment/${id}`),

    listFiles:  (id)           => api.get(`/equipment/${id}/files`),
    uploadFiles: (id, fileList) => {
      const fd = new FormData();
      [...fileList].forEach(f => fd.append('files', f));
      return api.upload(`/equipment/${id}/files`, fd);
    },
    deleteFile: (id, fileId)   => api.delete(`/equipment/${id}/files/${fileId}`),
    fileUrl:    (id, fileId, opts) => api.fileUrl(`/equipment/${id}/files/${fileId}/download`, opts),
  },
};

export default api;
